import type { CoachingNote, FeedbackReview, MatchSummary } from "@/lib/types";
import { NOTE_CATEGORY_LABELS } from "@/features/coach-review/components/Composer";

/**
 * Pure helpers behind the `/feedback` viewer surfaces.
 *
 * Extracted from the tab components so the non-trivial bits get
 * Vitest coverage without a render harness:
 *   - `summarySections` — which match-summary recap fields show,
 *     in order (the Match Summaries card preview + full view).
 *   - `reviewedNoteIds` / `reviewedPlaylistIds` — derive the
 *     reviewed-id sets from a `/feedback` bundle's `reviews[]`
 *     (drives the Reviewed/New pills).
 *   - `noteDisplayTitle` — the observation-aware title fallback
 *     chain for a viewer note card.
 */

/** The recap fields a match summary can carry, in display order.
 * `summarySections` keeps only the non-empty ones. */
const SUMMARY_FIELDS: ReadonlyArray<[keyof MatchSummary, string]> = [
  ["team_positives", "What went well"],
  ["team_improvements", "What we can improve"],
  ["training_focus", "Training focus"],
  ["body", "Coach recap"],
];

/** `[label, value]` pairs for a match summary's non-empty recap
 * fields, in display order. Ports legacy `_summarySectionData`. */
export function summarySections(s: MatchSummary): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [key, label] of SUMMARY_FIELDS) {
    const value = (s[key] as string | undefined)?.trim();
    if (value) out.push([label, value]);
  }
  return out;
}

/** The set of note ids the viewer has marked reviewed. */
export function reviewedNoteIds(reviews: FeedbackReview[]): Set<number> {
  return new Set(
    reviews
      .map((r) => r.note_id)
      .filter((id): id is number => typeof id === "number"),
  );
}

/** The set of playlist ids the viewer has marked reviewed. */
export function reviewedPlaylistIds(
  reviews: FeedbackReview[],
): Set<number> {
  return new Set(
    reviews
      .map((r) => r.playlist_id)
      .filter((id): id is number => typeof id === "number"),
  );
}

/** A viewer note card's display title — handles the observation
 * fallback chain (`title` → `event_title` → "Observation note";
 * video notes fall back to "(untitled)"). */
export function noteDisplayTitle(n: CoachingNote): string {
  const isObservation = (n.note_context ?? "video") === "observation";
  return (
    n.title?.trim() ||
    (isObservation
      ? n.event_title?.trim() || "Observation note"
      : "(untitled)")
  );
}

/** The {primary, secondary} pair driving a viewer detail body's
 * "Summary" + "Additional detail" sections. Ports legacy
 * `_feedbackNoteSummary`:
 *   - primary = `player_summary` (preferred — the age-appropriate
 *     player-facing line) OR `body` (when no player_summary).
 *   - secondary = `body` when `player_summary` was the primary AND
 *     `body` is distinct/non-empty (so the coach's longer note
 *     surfaces as "Additional detail" rather than getting hidden).
 */
export function feedbackNoteSummary(n: CoachingNote): {
  primary: string;
  secondary: string;
} {
  const playerSummary = (n.player_summary ?? "").trim();
  const body = (n.body ?? "").trim();
  if (playerSummary) {
    return {
      primary: playerSummary,
      secondary: body && body !== playerSummary ? body : "",
    };
  }
  return { primary: body, secondary: "" };
}

/** Display label for a note category. Falls back to a title-cased
 * version of the raw code so a forward-compat payload from a
 * future migration still renders sensibly. */
export function categoryLabel(category: string | null | undefined): string {
  if (!category) return "";
  const known = (NOTE_CATEGORY_LABELS as Record<string, string>)[category];
  if (known) return known;
  return (
    category.charAt(0).toUpperCase() +
    category.slice(1).replace(/_/g, " ")
  );
}

/** Viewer-facing "context pill" label for a note — drives the head
 * row in the unified detail body. Mirrors legacy
 * `_observationContextLabel` for observations and falls through to
 * "Video note" for video notes. */
export function contextPillLabel(n: CoachingNote): string {
  const isObservation = (n.note_context ?? "video") === "observation";
  if (!isObservation) return "Video note";
  if (
    n.event_type === "tactical" &&
    n.tactical_board_json &&
    ((n.tactical_board_json.tokens?.length ?? 0) > 0 ||
      (n.tactical_board_json.shapes?.length ?? 0) > 0)
  ) {
    return "Tactical observation";
  }
  if (n.event_type) {
    const t = String(n.event_type);
    return `${t.charAt(0).toUpperCase()}${t.slice(1)} observation`;
  }
  return "Coach observation";
}

/** Display label for a match summary's `visibility` field.
 * Reuses the same label set the coach Match Summary composer
 * offers so coach + viewer surfaces stay in lockstep. */
const SUMMARY_VISIBILITY_LABELS: Record<string, string> = {
  private: "Coach-only",
  team: "Team",
  unlisted: "Unlisted",
  player: "Players only",
};
export function summaryVisibilityLabel(
  visibility: string | null | undefined,
): string {
  if (!visibility) return "";
  return SUMMARY_VISIBILITY_LABELS[visibility] || visibility;
}

/** Compact date label for "Updated …" meta lines. ISO in → short
 * locale date out. Empty string for missing/unparseable inputs so
 * the caller can safely template into a `·`-separated meta string
 * without an undefined-or-NaN leak. */
export function shortDateLabel(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
